import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PeriodoHeader from '../components/PeriodoHeader';
import { usePeriodoComissionamentos } from '../hooks/usePeriodoComissionamentos';
import { Esqueleto } from '../../../components/esqueleto/Esqueleto';
import DadosAtualizadosEm from '../components/DadosAtualizadosEm';
import SincronizarBoletosButton from '../components/SincronizarBoletosButton';
import { getResumoPeriodo } from '../../../api/comissionamentosApi';
import { getCompetenciasStatus, CompetenciaStatus } from '../../../api/comissionamentosMovimentacoesApi';
import { mensagemDeErro, notify } from '../../../shared/feedback/notify';
// Esqueleto de página compartilhado (`recepcao-conte-do`, `recepcao-header-row`,
// `recepcao-title`). O import é explícito de propósito: depender de CSS carregado por
// outra tela é acoplamento invisível — foi o que originou o BUG-V74 na at-67.
import '../../recepcao/pages/Recepcao.css';
import './home.css';
import LineChart from '../components/LineChart';

// Formata DATE sem conversão de fuso (evita 1 dia a menos em UTC-3).
const fmtDataCurta = (iso: string | null) => {
  if (!iso) return '—';
  const [, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}`;
};

const ComissionamentosHome: React.FC = () => {
  const navigate = useNavigate();
  const [periodo, setPeriodo] = usePeriodoComissionamentos();
  // Sobe a cada disparo bem-sucedido: é o que faz o carimbo reconsultar sem F5.
  const [syncRecarga, setSyncRecarga] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [cooperativasQtde, setCooperativasQtde] = useState<number>(0);
  const [voluntariosQtde, setVoluntariosQtde] = useState<number>(0);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState<number>(780);

  useEffect(() => {
    if (!chartContainerRef.current) return;
    const updateWidth = () => {
      if (chartContainerRef.current) {
        const width = chartContainerRef.current.getBoundingClientRect().width - 40;
        setChartWidth(Math.max(300, width));
      }
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(chartContainerRef.current);

    return () => observer.disconnect();
  }, []);

  // KPIs monetários - Cooperativas
  const [cooperativaVitalicio, setCooperativaVitalicio] = useState<string>('0');
  const [cooperativaBonificacoes, setCooperativaBonificacoes] = useState<string>('0');
  const [cooperativaDescontos, setCooperativaDescontos] = useState<string>('0');
  const [cooperativaRepasse, setCooperativaRepasse] = useState<string>('0');

  // KPIs monetários - Voluntários
  const [voluntarioVitalicio, setVoluntarioVitalicio] = useState<string>('0');
  const [voluntarioBonificacoes, setVoluntarioBonificacoes] = useState<string>('0');
  const [voluntarioDescontos, setVoluntarioDescontos] = useState<string>('0');
  const [voluntarioRepasse, setVoluntarioRepasse] = useState<string>('0');

  // Resumo do período
  const [resumoTotalGerado, setResumoTotalGerado] = useState<string>('0');
  const [resumoTotalContratos, setResumoTotalContratos] = useState<number>(0);
  const [resumoNovosContratos, setResumoNovosContratos] = useState<number>(0);
  const [resumoContratosPerdidos, setResumoContratosPerdidos] = useState<number>(0);

  const formatCurrency = (v: string | number) => {
    const n = typeof v === 'string' ? Number(v) : v;
    if (!isFinite(n)) return '—';
    return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const getPrevPeriods = (basePeriodo: string, count = 6): string[] => {
    // basePeriodo: mm-yyyy
    const [mmStr, yyyyStr] = basePeriodo.split('-');
    let m = Number(mmStr);
    let y = Number(yyyyStr);
    const result: string[] = [];
    for (let i = 0; i < count; i++) {
      const mm = m < 10 ? `0${m}` : `${m}`;
      result.push(`${mm}-${y}`);
      m -= 1;
      if (m === 0) {
        m = 12;
        y -= 1;
      }
    }
    return result.reverse(); // mais antigo -> mais recente
  };

  const [cicloStatus, setCicloStatus] = useState<CompetenciaStatus | null>(null);
  // Estado próprio: `cicloStatus === null` significa DUAS coisas (ainda carregando ou a
  // consulta falhou — o toast avisa, e o card seguia exibindo "..." para sempre, com cara
  // de carregamento eterno). Sem esta flag o esqueleto teria o mesmo defeito.
  const [cicloCarregando, setCicloCarregando] = useState<boolean>(true);

  const [historicoPeriodos, setHistoricoPeriodos] = useState<string[]>([]);
  const [historicoNovos, setHistoricoNovos] = useState<number[]>([]);
  const [historicoPerdidos, setHistoricoPerdidos] = useState<number[]>([]);

  useEffect(() => {
    const fetchCounts = async () => {
      setLoading(true);
      setError(null);
      try {
        const resumo = await getResumoPeriodo(periodo);
        setCooperativasQtde(resumo?.cooperativa?.quantidade ?? 0);
        setVoluntariosQtde(resumo?.voluntario?.quantidade ?? 0);

        // KPIs monetários - Cooperativas
        setCooperativaVitalicio(resumo?.cooperativa?.valor_vitalicio ?? '0');
        setCooperativaBonificacoes(resumo?.cooperativa?.valor_bonificacoes ?? '0');
        setCooperativaDescontos(resumo?.cooperativa?.valor_descontos ?? '0');
        setCooperativaRepasse(resumo?.cooperativa?.valor_repasse ?? '0');

        // KPIs monetários - Voluntários
        setVoluntarioVitalicio(resumo?.voluntario?.valor_vitalicio ?? '0');
        setVoluntarioBonificacoes(resumo?.voluntario?.valor_bonificacoes ?? '0');
        setVoluntarioDescontos(resumo?.voluntario?.valor_descontos ?? '0');
        setVoluntarioRepasse(resumo?.voluntario?.valor_repasse ?? '0');

        // Resumo do período (base cooperativas)
        setResumoTotalGerado(resumo?.cooperativa?.total_gerado ?? '0');
        setResumoTotalContratos(resumo?.cooperativa?.total_contratos ?? 0);
        setResumoNovosContratos(resumo?.cooperativa?.novos_contratos ?? 0);
        setResumoContratosPerdidos(resumo?.cooperativa?.contratos_perdidos ?? 0);

        // histórico dos últimos 6 períodos (base cooperativa)
        const periods = getPrevPeriods(periodo, 6);
        setHistoricoPeriodos(periods);
        const requests = periods.map(p => getResumoPeriodo(p));
        const results = await Promise.all(requests);
        const novos = results.map(r => r?.cooperativa?.novos_contratos ?? 0);
        const perdidos = results.map(r => r?.cooperativa?.contratos_perdidos ?? 0);
        setHistoricoNovos(novos);
        setHistoricoPerdidos(perdidos);
      } catch (e: any) {
        console.warn('Erro ao buscar contagens:', e);
        // at-64: `e.message` de erro do axios é o status cru ("Request failed with status
        // code 500"); a faixa da tela existe e tem CSS, só mostrava a mensagem errada.
        setError(mensagemDeErro(e, 'Não foi possível carregar as contagens da competência. Recarregue a página; se persistir, acione a TI.'));
      } finally {
        setLoading(false);
      }
    };
    fetchCounts();
  }, [periodo]);

  useEffect(() => {
    let ativo = true;
    setCicloCarregando(true);
    getCompetenciasStatus(periodo)
      .then((r) => {
        if (ativo) setCicloStatus(r.data?.[0] ?? null);
      })
      .catch((error) => {
        if (!ativo) return;
        // Sem o aviso, o ciclo some da tela como se a competência não tivesse status.
        setCicloStatus(null);
        notify.errorFrom(
          error,
          'Não foi possível carregar o status do ciclo da competência. Recarregue a página; se persistir, acione a TI.'
        );
      })
      .finally(() => {
        if (ativo) setCicloCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, [periodo]);

  const goMovimentacoes = () => navigate('/comissionamentos/movimentacoes-retroativas');
  const goPagamentos = () => navigate('/comissionamentos/pagamentos');
  const goCooperativas = () => navigate('/comissionamentos/listas', { state: { defaultTab: 'cooperativa' } });
  const goVoluntarios = () => navigate('/comissionamentos/listas', { state: { defaultTab: 'voluntario' } });

  return (
    <div
      className="Frame1000004012"
      style={{
        height: 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        backgroundColor: 'transparent',
      }}
    >
      {/*
        Esqueleto COMPARTILHADO de página. `recepcao-conte-do` e `recepcao-header-row`
        substituem o contêiner `.comiss-home`, que replicava à mão os mesmos valores
        (122px/82px, fundo cinza, 100vh) — manter cópia local do esqueleto é o que fazia
        esta tela divergir das demais a cada ajuste. A classe local abaixo guarda só o
        respiro inferior, que o esqueleto compartilhado não tem.
      */}
      <div className="ConteDo recepcao-conte-do comiss-home-conte-do">
        {/* Período fica à direita do título, como estava no `.home-header`. */}
        <div className="recepcao-header-row" style={{ justifyContent: 'space-between' }}>
          <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Comissionamentos
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* O botão fica só aqui, na porta de entrada do módulo (decisão do Victor,
                03/09/2026): sincronizar é ação de manutenção, e repeti-la nas 6 telas viraria
                ruído. O CARIMBO, esse sim, aparece em todas. */}
            <SincronizarBoletosButton onDisparado={() => setSyncRecarga((n) => n + 1)} />
            <PeriodoHeader periodo={periodo} onChange={setPeriodo} />
          </div>
        </div>
        <DadosAtualizadosEm recarregarEm={syncRecarga} />
        <div className="home-grid">
          {/* Resumo do Período - card maior ocupando toda a largura */}
          {/* `aria-busy` + `role="status"` anunciam o carregamento uma vez por card; as peças
              de esqueleto são `aria-hidden` (ver components/esqueleto/Esqueleto.tsx). */}
          <div className="home-card lilac home-card-full" role="status" aria-busy={loading}>
            <div className="home-card-left">
              <span className="home-badge">Resumo do Período</span>
              <span className="home-desc">Base cooperativas</span>
              {loading && <span className="esqueleto-sr-only">Carregando o resumo do período</span>}
              <div className="home-kpis home-kpis-4">
                <div className="home-kpi">
                  <span className="home-kpi-label">Total gerado</span>
                  <span className="home-kpi-value">
                    {loading ? <Esqueleto variante="valor" largura="140px" /> : formatCurrency(resumoTotalGerado)}
                  </span>
                </div>
                <div className="home-kpi">
                  <span className="home-kpi-label">Total contratos</span>
                  <span className="home-kpi-value">
                    {loading ? <Esqueleto variante="valor" largura="70px" /> : resumoTotalContratos}
                  </span>
                </div>
                <div className="home-kpi">
                  <span className="home-kpi-label">Novos</span>
                  <span className="home-kpi-value highlight-green">
                    {loading ? <Esqueleto variante="valor" largura="60px" /> : `+${resumoNovosContratos}`}
                  </span>
                </div>
                <div className="home-kpi">
                  <span className="home-kpi-label">Perdidos</span>
                  <span className="home-kpi-value highlight-red">
                    {loading ? <Esqueleto variante="valor" largura="60px" /> : `-${resumoContratosPerdidos}`}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Ciclo da competência: corte, janela de congelamento e boletos em aberto */}
          <div className="home-card ciclo home-card-full" onClick={goMovimentacoes} role="status" aria-busy={cicloCarregando}>
            <div className="home-card-left">
              <span className="home-badge">Ciclo da competência {cicloStatus ? `(${cicloStatus.periodo})` : ''}</span>
              {cicloCarregando && <span className="esqueleto-sr-only">Carregando o ciclo da competência</span>}
              <span className="home-desc">
                Repasse fecha por data de pagamento. Após o corte, o sistema ainda espera boletos
                atrasados até congelar; depois disso, mudanças viram movimentação retroativa.
              </span>
              <div className="home-kpis home-kpis-4">
                {/* Fora do carregamento, "—" em vez de "...": ausência de dado (a consulta
                    falhou, com toast) não pode ter a cara de carregamento eterno. */}
                <div className="home-kpi">
                  <span className="home-kpi-label">Corte</span>
                  <span className="home-kpi-value">
                    {cicloCarregando ? (
                      <Esqueleto variante="valor" largura="70px" />
                    ) : cicloStatus ? (
                      fmtDataCurta(cicloStatus.corte)
                    ) : (
                      '—'
                    )}
                  </span>
                </div>
                <div className="home-kpi">
                  <span className="home-kpi-label">{cicloStatus?.congelado ? 'Fechado em' : 'Congela em'}</span>
                  <span className="home-kpi-value">
                    {cicloCarregando ? (
                      <Esqueleto variante="valor" largura="70px" />
                    ) : cicloStatus ? (
                      fmtDataCurta(cicloStatus.congelado ? cicloStatus.fechado_em : cicloStatus.congela_em)
                    ) : (
                      '—'
                    )}
                  </span>
                </div>
                <div className="home-kpi">
                  <span className="home-kpi-label">Situação</span>
                  <span className={`home-kpi-value ${cicloStatus?.congelado ? 'highlight-green' : 'highlight-orange'}`}>
                    {cicloCarregando ? (
                      <Esqueleto variante="valor" largura="90px" />
                    ) : cicloStatus ? (
                      cicloStatus.congelado ? 'Fechado' : 'Em espera'
                    ) : (
                      '—'
                    )}
                  </span>
                </div>
                <div className="home-kpi">
                  <span className="home-kpi-label">Boletos em aberto</span>
                  <span className="home-kpi-value">
                    {cicloCarregando ? (
                      <Esqueleto variante="valor" largura="60px" />
                    ) : cicloStatus ? (
                      cicloStatus.boletos_em_aberto
                    ) : (
                      '—'
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Cooperativas */}
          <div className="home-card orange" onClick={goCooperativas} role="status" aria-busy={loading}>
            <div className="home-card-left">
              <div className="home-card-header">
                <span className="home-card-icon">👥</span>
                <span className="home-badge">
                  Cooperativas {loading ? <Esqueleto variante="texto" largura="46px" /> : `(${cooperativasQtde})`}
                </span>
              </div>
              <span className="home-desc">Acessar lista de cooperativas</span>
              {loading && <span className="esqueleto-sr-only">Carregando os valores das cooperativas</span>}
              <div className="home-kpis">
                <div className="home-kpi">
                  <span className="home-kpi-label">Vitalício</span>
                  <span className="home-kpi-value">
                    {loading ? <Esqueleto variante="valor" largura="120px" /> : formatCurrency(cooperativaVitalicio)}
                  </span>
                </div>
                <div className="home-kpi">
                  <span className="home-kpi-label">Bonificações</span>
                  <span className="home-kpi-value">
                    {loading ? <Esqueleto variante="valor" largura="120px" /> : formatCurrency(cooperativaBonificacoes)}
                  </span>
                </div>
                <div className="home-kpi">
                  <span className="home-kpi-label">Descontos</span>
                  <span className="home-kpi-value">
                    {loading ? <Esqueleto variante="valor" largura="120px" /> : formatCurrency(cooperativaDescontos)}
                  </span>
                </div>
                <div className="home-kpi">
                  <span className="home-kpi-label">Repasse</span>
                  <span className="home-kpi-value highlight-blue">
                    {loading ? <Esqueleto variante="valor" largura="120px" /> : formatCurrency(cooperativaRepasse)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Voluntários */}
          <div className="home-card green" onClick={goVoluntarios} role="status" aria-busy={loading}>
            <div className="home-card-left">
              <div className="home-card-header">
                <span className="home-card-icon">🤝</span>
                <span className="home-badge">
                  Voluntários {loading ? <Esqueleto variante="texto" largura="46px" /> : `(${voluntariosQtde})`}
                </span>
              </div>
              <span className="home-desc">Acessar lista de voluntários</span>
              {loading && <span className="esqueleto-sr-only">Carregando os valores dos voluntários</span>}
              <div className="home-kpis">
                <div className="home-kpi">
                  <span className="home-kpi-label">Vitalício</span>
                  <span className="home-kpi-value">
                    {loading ? <Esqueleto variante="valor" largura="120px" /> : formatCurrency(voluntarioVitalicio)}
                  </span>
                </div>
                <div className="home-kpi">
                  <span className="home-kpi-label">Bonificações</span>
                  <span className="home-kpi-value">
                    {loading ? <Esqueleto variante="valor" largura="120px" /> : formatCurrency(voluntarioBonificacoes)}
                  </span>
                </div>
                <div className="home-kpi">
                  <span className="home-kpi-label">Descontos</span>
                  <span className="home-kpi-value">
                    {loading ? <Esqueleto variante="valor" largura="120px" /> : formatCurrency(voluntarioDescontos)}
                  </span>
                </div>
                <div className="home-kpi">
                  <span className="home-kpi-label">Repasse</span>
                  <span className="home-kpi-value highlight-blue">
                    {loading ? <Esqueleto variante="valor" largura="120px" /> : formatCurrency(voluntarioRepasse)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Pagamentos */}
          <div className="home-card pink" onClick={goPagamentos}>
            <div className="home-card-left">
              <div className="home-card-header">
                <span className="home-card-icon">💰</span>
                <span className="home-badge">Pagamentos</span>
              </div>
              <span className="home-desc">Visualizar pagamentos e status</span>
            </div>
          </div>
        </div>
        <div className="home-chart" ref={chartContainerRef} role="status" aria-busy={loading}>
          <div className="home-chart-title">Evolução (últimos 6 períodos) — Cooperativas</div>
          <div className="home-chart-legend">
            <span className="legend-item" style={{ color: '#0b6cff' }}>Novos contratos</span>
            <span className="legend-item" style={{ color: '#ff7a00' }}>Contratos perdidos</span>
          </div>
          {loading ? (
            <>
              {/* Mesma altura do gráfico (240px): placeholder menor faria a tela pular
                  quando a série chegar. Sem ele, o LineChart desenha eixos vazios, que se
                  leem como "não houve movimento no período". */}
              <span className="esqueleto-sr-only">Carregando a evolução dos últimos 6 períodos</span>
              <Esqueleto variante="bloco" altura="240px" />
            </>
          ) : (
            <LineChart
              labels={historicoPeriodos}
              series={[
                { name: 'Novos', color: '#0b6cff', data: historicoNovos },
                { name: 'Perdidos', color: '#ff7a00', data: historicoPerdidos },
              ]}
              width={chartWidth}
              height={240}
            />
          )}
        </div>
        {error && <div className="home-error">{error}</div>}
      </div>
    </div>
  );
};

export default ComissionamentosHome;